import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Container,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import {
  DOCUMENT,
  PAGE_CONTAINER_MAX_WIDTH,
  documentPageContainerSx,
} from '../constants/documentTheme';
import { partnerPageInnerSx } from '../components/PartnerListChrome';
import { howToManual } from '../content/howToManual';

const articleBlob = (section, article) => [
  section.title,
  article.title,
  article.summary,
  ...(article.steps || []),
].join(' ').toLowerCase();

const HowToGuide = ({ role = 'client' }) => {
  const { t, i18n } = useTranslation();
  const [query, setQuery] = useState('');
  const normalized = query.trim().toLowerCase();
  const manual = howToManual(i18n.language, role);

  const sections = useMemo(() => (
    (manual.sections || [])
      .map((section) => ({
        ...section,
        articles: (section.articles || []).filter((article) => (
          !normalized || articleBlob(section, article).includes(normalized)
        )),
      }))
      .filter((section) => section.articles.length > 0)
  ), [manual, normalized]);

  const shellSx = role === 'partner'
    ? { ...partnerPageInnerSx, pt: { xs: 3, md: 4 } }
    : documentPageContainerSx;

  return (
    <Container
      maxWidth={role === 'partner' ? false : PAGE_CONTAINER_MAX_WIDTH}
      sx={shellSx}
      data-testid="how-to-guide"
    >
      <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: { xs: 32, md: 40 }, mb: 1 }}>
        {t(role === 'partner' ? 'howTo.partnerTitle' : 'howTo.clientTitle')}
      </Typography>
      <Typography sx={{ fontSize: 15, color: DOCUMENT.ink2, maxWidth: 720, mb: 3 }}>
        {t(role === 'partner' ? 'howTo.partnerIntro' : 'howTo.clientIntro')}
      </Typography>
      <TextField
        fullWidth
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={t('howTo.searchPlaceholder')}
        variant="standard"
        inputProps={{ 'data-testid': 'how-to-search' }}
        sx={{ maxWidth: 480, mb: 4 }}
      />
      {sections.length === 0 ? (
        <Typography sx={{ color: DOCUMENT.ink3 }} data-testid="how-to-empty">
          {t('howTo.noResults')}
        </Typography>
      ) : (
        sections.map((section) => (
          <Box key={section.id} data-testid={`how-to-section-${section.id}`} sx={{ mb: 4 }}>
            <Typography sx={{ fontSize: 12, letterSpacing: '0.16em', textTransform: 'uppercase', color: DOCUMENT.accent, mb: 1 }}>
              {section.title}
            </Typography>
            {section.articles.map((article) => (
              <Accordion
                key={`${article.id}-${normalized ? 'match' : 'rest'}`}
                disableGutters
                elevation={0}
                defaultExpanded={Boolean(normalized)}
                data-testid={`how-to-article-${section.id}-${article.id}`}
                sx={{
                  bgcolor: 'transparent',
                  borderBottom: `1px solid ${DOCUMENT.rule}`,
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box>
                    <Typography sx={{ fontSize: 16 }}>{article.title}</Typography>
                    <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3 }}>{article.summary}</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
                    {(article.steps || []).map((step) => (
                      <Typography component="li" key={step} sx={{ fontSize: 14.5, lineHeight: 1.55, color: DOCUMENT.ink2, mb: 1.25 }}>
                        {step}
                      </Typography>
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        ))
      )}
    </Container>
  );
};

export default HowToGuide;
